// ============================================================================
// Instagram DM Webhook Adapter
//
// Implements the `WebhookAdapter` contract so the ingress pipeline can:
//   1. Verify the `x-hub-signature-256` HMAC against the raw request body
//      using the app secret (from `env.INSTAGRAM_APP_SECRET`, falling back to
//      `FB_APP_SECRET` because Meta apps share a single secret across FB +
//      Instagram surfaces).
//   2. Parse the Instagram webhook envelope (`entry[].messaging[]` — same
//      shape as Messenger) plus IG-specific changes (`story_mention`,
//      `messaging_reactions`, `messaging_referral`) into canonical capability
//      events the chat + ai modules consume.
//
// Canonical docs:
//   - https://developers.facebook.com/docs/instagram-platform/webhooks
//   - https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login/messaging-api
//
// All `eventId` values are retry-stable: they NEVER include `timestamp` so
// Meta's redelivery (which re-stamps the delivery timestamp) collapses to the
// same dedupe key.
// ============================================================================

import type {
  MessageAttachment,
  MessageReadEvent,
  MessageReceivedEvent,
} from '../../capabilities/messaging'
import {
  hmac,
  hexToBytes,
  parsePrefixedSignature,
  timingSafeEqualBytes,
} from '../../services/webhook-verifier'
import type {
  ParsedWebhookEvent,
  WebhookAdapter,
  WebhookAdapterContext,
} from '../../services/webhook-adapters'

// ============================================================================
// Raw Instagram payload shape — matches the subset of fields we care about.
// Everything is `unknown`-safe: Meta occasionally ships new fields (or sends
// test pings with no `messaging` key) and the parser must tolerate both.
// ============================================================================

interface InstagramWebhookPayload {
  /** `instagram` for IG webhooks (vs `page` for Messenger). */
  object?: string
  entry?: ReadonlyArray<InstagramEntry>
}

interface InstagramEntry {
  /** Business IG user id that owns the messaging events. */
  id?: string
  /** Millisecond timestamp the webhook was emitted. */
  time?: number
  /** Standard messaging events (DM, reaction, read, referral, story mention). */
  messaging?: ReadonlyArray<InstagramMessagingEvent>
  /** Change-notification events (subscribed fields). IG sometimes routes story
   *  mentions here instead of through `messaging`. We tolerate both. */
  changes?: ReadonlyArray<InstagramChangeEvent>
}

interface InstagramMessagingEvent {
  sender?: { id?: string }
  recipient?: { id?: string }
  /** Milliseconds. */
  timestamp?: number
  message?: InstagramMessage
  reaction?: InstagramReaction
  read?: InstagramRead
  /** Story mention — user mentions the business in their IG Story. */
  story_mention?: InstagramStoryMention
  /** Messaging referral — user came via ad / link / story CTA. */
  messaging_referral?: InstagramReferral
  /** IG sometimes nests the referral under `referral` instead. */
  referral?: InstagramReferral
  postback?: InstagramPostback
}

interface InstagramMessage {
  mid?: string
  text?: string
  attachments?: ReadonlyArray<InstagramAttachment>
  /** IG sends `is_echo` on messages sent by the business's own Send API. */
  is_echo?: boolean
  /** IG story reply — user replied to the business's story via DM. */
  reply_to?: { story?: { url?: string; id?: string } }
  quick_reply?: { payload?: string }
}

interface InstagramAttachment {
  type?: string
  payload?: { url?: string; title?: string; [k: string]: unknown }
  title?: string
}

interface InstagramReaction {
  /** `react` (add) or `unreact` (remove). */
  action?: string
  /** Which emoji the user picked — only present on `react`. */
  reaction?: string
  emoji?: string
  /** The message id the reaction was attached to. */
  mid?: string
}

interface InstagramRead {
  /** Meta started sending `mid` on reads from ~v17; older envelopes only
   *  carry the numeric watermark. We accept both and prefer `mid` when present. */
  mid?: string
  watermark?: number
}

interface InstagramStoryMention {
  /** CDN URL of the story asset the user mentioned the business in. */
  url?: string
  /** IG story id — stable for dedupe. */
  id?: string
  /** Story mention attachment shape (some envelopes nest it here). */
  attachments?: ReadonlyArray<InstagramAttachment>
}

interface InstagramReferral {
  ref?: string
  source?: string
  type?: string
  ads_context_data?: Record<string, unknown>
}

interface InstagramPostback {
  title?: string
  payload?: string
  mid?: string
}

interface InstagramChangeEvent {
  field?: string
  value?: {
    sender?: { id?: string }
    recipient?: { id?: string }
    timestamp?: number
    mid?: string
    text?: string
    attachments?: ReadonlyArray<InstagramAttachment>
    [k: string]: unknown
  }
}

// ============================================================================
// verifyWebhook
// ============================================================================

async function verifyWebhook(ctx: WebhookAdapterContext): Promise<boolean> {
  // Missing secret → fail closed. Response timings stay uniform if we return
  // false rather than throwing — the ingress pipeline treats both the same,
  // but `false` prevents an exception from being logged.
  const appSecret = pickSecret(ctx.env)
  if (!appSecret) return false

  const header = ctx.headers.get('x-hub-signature-256')
  if (!header) return false

  const candidateHex = parsePrefixedSignature(header, 'sha256')
  if (!candidateHex) return false

  // Convert the hex candidate to raw bytes, then constant-time compare against
  // a freshly computed HMAC. Comparing as strings would leak timing info; the
  // Meta signature header is attacker-controllable.
  let candidateBytes: Uint8Array
  try {
    candidateBytes = hexToBytes(candidateHex.trim())
  } catch {
    return false
  }

  let expectedBytes: Uint8Array
  try {
    expectedBytes = await hmac(appSecret, ctx.rawBody, 'SHA-256')
  } catch {
    return false
  }

  // Length check BEFORE timing-safe compare. `timingSafeEqualBytes`
  // short-circuits on length mismatch (non-constant-time), which would
  // otherwise leak whether the attacker supplied the right number of bytes
  // vs. the right contents. By ALWAYS computing the expected HMAC above and
  // then bailing here, the timing of both paths (length mismatch + length
  // match with wrong bytes) is dominated by the HMAC computation, not the
  // compare. (Round 1 H4 fix.)
  if (candidateBytes.length !== expectedBytes.length) return false

  return timingSafeEqualBytes(candidateBytes, expectedBytes)
}

/**
 * Pick the app secret from the env bag, tolerating absence / non-string
 * values. Meta apps share ONE secret across FB + Instagram surfaces, so
 * operators commonly point both at `FB_APP_SECRET`. We let them override
 * explicitly via `INSTAGRAM_APP_SECRET` for setups that rotate the IG app
 * independently.
 */
function pickSecret(env: Record<string, unknown>): string | null {
  const explicit = env?.INSTAGRAM_APP_SECRET
  if (typeof explicit === 'string' && explicit.length > 0) return explicit
  const fallback = env?.FB_APP_SECRET
  if (typeof fallback === 'string' && fallback.length > 0) return fallback
  return null
}

// ============================================================================
// parseWebhook
// ============================================================================

async function parseWebhook(
  ctx: WebhookAdapterContext,
): Promise<ParsedWebhookEvent[]> {
  const text = new TextDecoder().decode(new Uint8Array(ctx.rawBody))

  // JSON.parse throws on malformed bodies — the ingress pipeline treats any
  // parser exception as `invalid_payload` (400). Do NOT swallow.
  const payload = JSON.parse(text) as InstagramWebhookPayload

  const entries = Array.isArray(payload?.entry) ? payload.entry : []
  const events: ParsedWebhookEvent[] = []

  for (const entry of entries) {
    const igBusinessId = typeof entry?.id === 'string' ? entry.id : ''
    const messaging = Array.isArray(entry?.messaging) ? entry.messaging : []

    for (const event of messaging) {
      const senderId = typeof event?.sender?.id === 'string' ? event.sender.id : ''
      const timestamp = typeof event?.timestamp === 'number' ? event.timestamp : 0
      const receivedAt = isoFromTimestamp(timestamp)

      // Capture referral metadata once so downstream handlers (story_mention,
      // message.received) can attach it. IG spreads referrals between two keys
      // depending on the event family; merge them into one shape.
      const referral = event.messaging_referral ?? event.referral

      // 1. Story mention — user mentions the business in their IG Story.
      //    Surface as `message.received` with metadata.kind='story_mention'
      //    (no dedicated capability event type exists in our messaging spec
      //    yet; the chat module branches on the metadata flag).
      if (event.story_mention) {
        const storyId =
          typeof event.story_mention.id === 'string' ? event.story_mention.id : ''
        const storyUrl =
          typeof event.story_mention.url === 'string' ? event.story_mention.url : ''
        // Retry-stable eventId — prefer the IG story id, fall back to the sender
        // + URL hash. We intentionally do NOT include `timestamp` so Meta
        // redeliveries collapse.
        const eventId =
          storyId.length > 0
            ? `story_mention:${storyId}`
            : `story_mention:${encodeURIComponent(senderId)}:${encodeURIComponent(storyUrl)}`

        const attachments: MessageAttachment[] = []
        if (storyUrl.length > 0) {
          attachments.push({ type: 'image', url: storyUrl, alt: 'story_mention' })
        }
        // Some envelopes nest additional media under story_mention.attachments
        const extra = toAttachments(event.story_mention.attachments)
        for (const a of extra) attachments.push(a)

        const storyPayload: MessageReceivedEvent & {
          metadata?: Record<string, unknown>
        } = {
          capability: 'messaging',
          event: 'message.received',
          instanceId: '',
          companyId: '',
          receivedAt,
          providerMessageId: eventId,
          senderId,
          ...(attachments.length > 0 ? { attachments } : {}),
          metadata: {
            kind: 'story_mention',
            igBusinessId,
            storyId: storyId || undefined,
            storyUrl: storyUrl || undefined,
            ...(referral ? { referral: sanitizeReferral(referral) } : {}),
          },
        }

        events.push({
          eventId,
          eventType: 'message.received',
          capability: 'messaging',
          payload: storyPayload,
        })
        continue
      }

      // 2. Inbound DM (text / attachments / story reply / quick reply tap)
      if (event.message) {
        // Issue #249 M3: check mid FIRST, THEN echo. A malformed payload
        // without `mid` is a harder error (we lose dedupe + retry stability);
        // the echo check is a legitimate business-owned loopback filter.
        // Order matters for observability: missing-mid should surface as a
        // silent skip we can grep for in audit logs, not get hidden behind
        // the echo guard.
        const mid = typeof event.message.mid === 'string' ? event.message.mid : ''
        if (mid.length === 0) continue

        // Skip echoes (our own outbound mirrored back) — they would loop back
        // into the messaging capability as a new inbound.
        if (event.message.is_echo === true) continue

        const attachments = toAttachments(event.message.attachments)

        const metadata: Record<string, unknown> = { igBusinessId }
        if (event.message.reply_to?.story) {
          metadata.kind = 'story_reply'
          metadata.story = {
            id: event.message.reply_to.story.id,
            url: event.message.reply_to.story.url,
          }
        }
        if (referral) {
          metadata.referral = sanitizeReferral(referral)
        }
        if (event.message.quick_reply?.payload) {
          metadata.quickReplyPayload = event.message.quick_reply.payload
        }

        const receivedPayload: MessageReceivedEvent & {
          metadata?: Record<string, unknown>
        } = {
          capability: 'messaging',
          event: 'message.received',
          instanceId: '',
          companyId: '',
          receivedAt,
          providerMessageId: mid,
          senderId,
          ...(event.message.text !== undefined ? { text: event.message.text } : {}),
          ...(attachments.length > 0 ? { attachments } : {}),
          ...(Object.keys(metadata).length > 1 ? { metadata } : {}),
        }

        events.push({
          eventId: mid,
          eventType: 'message.received',
          capability: 'messaging',
          payload: receivedPayload,
        })
        continue
      }

      // 3. Reaction — user added or removed an emoji reaction on a message.
      //    Meta uses action: 'react' | 'unreact'. We emit
      //    `messaging.reaction.added` / `messaging.reaction.removed` via a
      //    best-effort shape (the MessagingCapability doesn't declare these
      //    events yet — P11 will formalise them; until then downstream
      //    subscribers read the raw payload).
      if (event.reaction) {
        const action = typeof event.reaction.action === 'string' ? event.reaction.action : ''
        const reactionMid =
          typeof event.reaction.mid === 'string' ? event.reaction.mid : ''
        if (reactionMid.length === 0) continue

        const isAdded = action !== 'unreact'
        const eventType = isAdded ? 'reaction.added' : 'reaction.removed'
        const eventId = `reactions:${senderId}:${reactionMid}`

        events.push({
          eventId,
          eventType,
          capability: 'messaging',
          payload: {
            capability: 'messaging',
            event: eventType,
            instanceId: '',
            companyId: '',
            providerMessageId: reactionMid,
            senderId,
            reaction: event.reaction.reaction ?? event.reaction.emoji,
            at: receivedAt,
            metadata: { igBusinessId, kind: isAdded ? 'reaction_added' : 'reaction_removed' },
          },
        })
        continue
      }

      // 4. Read receipt. IG sometimes sends a `mid` (message being read);
      //    fall back to the watermark for older envelopes. Retry-stable
      //    eventId uses sender + mid OR sender + watermark (no timestamp).
      if (event.read) {
        const readMid = typeof event.read.mid === 'string' ? event.read.mid : ''
        const watermark =
          typeof event.read.watermark === 'number' ? event.read.watermark : 0
        const providerMessageId =
          readMid.length > 0
            ? readMid
            : watermark > 0
              ? String(watermark)
              : ''
        if (providerMessageId.length === 0) continue

        const eventId =
          readMid.length > 0
            ? `reads:${senderId}:${readMid}`
            : `reads:${senderId}:${watermark}`

        const readPayload: MessageReadEvent = {
          capability: 'messaging',
          event: 'message.read',
          instanceId: '',
          companyId: '',
          providerMessageId,
          readAt: isoFromTimestamp(
            watermark > 0 ? watermark : timestamp,
          ),
        }
        events.push({
          eventId,
          eventType: 'message.read',
          capability: 'messaging',
          payload: readPayload,
        })
        continue
      }

      // 5. Standalone referral (no message attached) — Meta sends this when
      //    a user taps an ads/link CTA that lands on the business IG profile.
      //    We capture it in metadata but do NOT emit as a dedicated event
      //    type; the referral is attached to the NEXT inbound message.
      //    (Swallowing here keeps dedupe simple.)
      if (referral && !event.message && !event.story_mention) {
        // noop — intentional: standalone referrals are not user-actionable.
        continue
      }

      // 6. Postback (rare on IG — persistent menu / ad CTA tap). Use the same
      //    pattern as the FB adapter for consistency.
      if (event.postback) {
        const postbackPayload =
          typeof event.postback.payload === 'string' ? event.postback.payload : ''
        const mid = typeof event.postback.mid === 'string' ? event.postback.mid : ''
        const eventId =
          mid.length > 0
            ? mid
            : `postback:${encodeURIComponent(senderId)}:${encodeURIComponent(postbackPayload)}`

        const text =
          postbackPayload.length > 0
            ? postbackPayload
            : typeof event.postback.title === 'string'
              ? event.postback.title
              : ''

        const postbackEvent: MessageReceivedEvent & {
          metadata?: Record<string, unknown>
        } = {
          capability: 'messaging',
          event: 'message.received',
          instanceId: '',
          companyId: '',
          receivedAt,
          providerMessageId: eventId,
          senderId,
          text,
          metadata: { kind: 'postback', igBusinessId },
        }

        events.push({
          eventId,
          eventType: 'message.received',
          capability: 'messaging',
          payload: postbackEvent,
        })
        continue
      }

      // Any other shape is ignored — IG occasionally ships new event kinds
      // (e.g. `optin`) that we haven't mapped yet. Silent skip is safer than
      // guessing: the ingress audit log still records the raw payload.
    }

    // Some IG apps route a subset of events through `changes[]` instead of
    // `messaging[]`. Walk it best-effort; today we only accept `messages`
    // fields (the rest is provider noise).
    const changes = Array.isArray(entry?.changes) ? entry.changes : []
    for (const change of changes) {
      if (change.field !== 'messages' || !change.value) continue
      const value = change.value
      const mid = typeof value.mid === 'string' ? value.mid : ''
      if (mid.length === 0) continue
      const senderId =
        typeof value.sender?.id === 'string' ? value.sender.id : ''
      const ts = typeof value.timestamp === 'number' ? value.timestamp : 0

      const receivedPayload: MessageReceivedEvent & {
        metadata?: Record<string, unknown>
      } = {
        capability: 'messaging',
        event: 'message.received',
        instanceId: '',
        companyId: '',
        receivedAt: isoFromTimestamp(ts),
        providerMessageId: mid,
        senderId,
        ...(typeof value.text === 'string' ? { text: value.text } : {}),
        ...(Array.isArray(value.attachments)
          ? { attachments: toAttachments(value.attachments) }
          : {}),
        metadata: { igBusinessId, source: 'changes' },
      }

      events.push({
        eventId: mid,
        eventType: 'message.received',
        capability: 'messaging',
        payload: receivedPayload,
      })
    }
  }

  return events
}

// ============================================================================
// Attachment mapping — IG shape → capability shape
// ============================================================================

function toAttachments(
  input: ReadonlyArray<InstagramAttachment> | undefined,
): MessageAttachment[] {
  if (!Array.isArray(input)) return []

  const out: MessageAttachment[] = []
  for (const raw of input) {
    const type = typeof raw?.type === 'string' ? raw.type : ''
    const url = typeof raw?.payload?.url === 'string' ? raw.payload.url : ''
    if (!url) continue

    switch (type) {
      case 'image': {
        const altTitle =
          typeof raw.title === 'string'
            ? raw.title
            : typeof raw.payload?.title === 'string'
              ? raw.payload.title
              : undefined
        out.push({ type: 'image', url, ...(altTitle ? { alt: altTitle } : {}) })
        break
      }
      // IG Reels + IG TV are videos. Meta sometimes labels the attachment
      // `ig_reel` or `video` depending on the source; map both to `video` so
      // the chat UI renders a video player instead of a static image.
      case 'ig_reel':
      case 'video':
        out.push({ type: 'video', url })
        break
      case 'audio':
        out.push({ type: 'audio', url })
        break
      case 'file':
      case 'share':
      case 'story_mention': {
        const filename =
          typeof raw.payload?.title === 'string' ? raw.payload.title : undefined
        out.push({ type: 'file', url, ...(filename ? { filename } : {}) })
        break
      }
      default:
        // Unknown attachment types fall back to `file` so the chat UI at
        // least gets a downloadable link. Keeps the adapter permissive when
        // Meta ships a new attachment shape.
        out.push({ type: 'file', url })
        break
    }
  }
  return out
}

// ============================================================================
// Referral sanitisation — strip ad secrets before passing into metadata
// ============================================================================

function sanitizeReferral(ref: InstagramReferral): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (typeof ref.ref === 'string') out.ref = ref.ref
  if (typeof ref.source === 'string') out.source = ref.source
  if (typeof ref.type === 'string') out.type = ref.type
  // `ads_context_data` can contain PII (post id, etc.). Pass through the
  // keys we explicitly whitelist; drop the rest.
  if (ref.ads_context_data && typeof ref.ads_context_data === 'object') {
    const adsOut: Record<string, unknown> = {}
    for (const key of ['ad_title', 'photo_url', 'video_url', 'post_id']) {
      if (key in ref.ads_context_data) {
        adsOut[key] = ref.ads_context_data[key]
      }
    }
    if (Object.keys(adsOut).length > 0) out.adsContext = adsOut
  }
  return out
}

// ============================================================================
// Time helpers
// ============================================================================

function isoFromTimestamp(msOrZero: number): string {
  if (!Number.isFinite(msOrZero) || msOrZero <= 0) {
    return new Date(0).toISOString()
  }
  return new Date(msOrZero).toISOString()
}

// ============================================================================
// Adapter export
// ============================================================================

export const instagramWebhookAdapter: WebhookAdapter = {
  verifyWebhook,
  parseWebhook,
}

// Named re-exports for tests + admin tooling that want to wrap one step.
export { verifyWebhook as verifyInstagramSignature }
export { parseWebhook as parseInstagramWebhook }
